/* Big Z live-inventory guard.
   Reads live variant inventory from /products/{handle}.js and clamps cart add/change
   payloads before the storefront UI can show quantities Shopify will reject later. */
(function () {
  if (window.bzInventoryGuard || !window.fetch) return;

  var nativeFetch = window.fetch.bind(window);
  var productCache = Object.create(null);
  var variantToHandle = Object.create(null);
  var variantFallbackLimits = Object.create(null);
  var cartPromise = null;
  var cartMutationQueue = Promise.resolve();
  var cartRepairPromise = null;
  var PRODUCT_CACHE_MS = 15000;
  var UNKNOWN_VARIANT_MAX_QTY = 10;

  // 2026-10-25 speed: the last cart this page heard about (any cart:update that carried the cart).
  // repairCartLimits() runs after every cart write. It used to broadcast cart:update even when it
  // found nothing to fix, and every listener reacted: the Horizon drawer re-rendered the whole
  // header (~118 KB of HTML) from the server, ride-along re-checked, the ship-date panel re-stamped
  // lines, and those writes came back through here and broadcast again. Now it only broadcasts when
  // it corrected a quantity or the cart differs from what the page last heard.
  // An empty cart at page load is known up front (snippets/bz-ride-along), so the first check after
  // a size pick on an empty-cart page doesn't count as news.
  var lastHeardSig = (window.BZRideCfg && window.BZRideCfg.itemCount === 0) ? '0|0|' : null;
  function cartSig(cart) {
    if (!cart || !cart.items) return null;
    var lines = [];
    for (var i = 0; i < cart.items.length; i++) lines.push((cart.items[i].key || cart.items[i].id) + 'x' + cart.items[i].quantity);
    return cart.item_count + '|' + cart.total_price + '|' + lines.join(',');
  }
  document.addEventListener('cart:update', function (event) {
    var heard = cartSig(event && event.detail && event.detail.resource);
    if (heard !== null) lastHeardSig = heard;
  });

  function GuardError(message, warnings) {
    this.name = 'BZInventoryGuardError';
    this.message = message || 'Could not update quantity.';
    this.warnings = warnings || [];
  }
  GuardError.prototype = Object.create(Error.prototype);
  GuardError.prototype.constructor = GuardError;

  function toInt(value, fallback) {
    var n = parseInt(value, 10);
    return isNaN(n) ? fallback : n;
  }

  function idString(value) {
    var n = toInt(value, 0);
    return n ? String(n) : '';
  }

  function productUrl(handle) {
    return '/products/' + encodeURIComponent(handle) + '.js';
  }

  function requestUrl(input) {
    try {
      if (typeof input === 'string') return new URL(input, window.location.origin);
      if (input && input.url) return new URL(input.url, window.location.origin);
    } catch (_e) {}
    return null;
  }

  function isCartAdd(url) {
    return !!url && /\/cart\/add(\.js)?$/.test(url.pathname);
  }

  function isCartChange(url) {
    return !!url && /\/cart\/change(\.js)?$/.test(url.pathname);
  }

  function isCartUpdate(url) {
    return !!url && /\/cart\/update(\.js)?$/.test(url.pathname);
  }

  function queueCartMutation(task) {
    var run = cartMutationQueue.catch(function () {}).then(function () {
      cartPromise = null;
      return task();
    }).then(function (response) {
      cartPromise = null;
      return response;
    }, function (error) {
      cartPromise = null;
      throw error;
    });

    cartMutationQueue = run.catch(function () {});
    return run;
  }

  function verifyCartMutation(response, quiet) {
    if (!response || !response.ok) return response;
    var options = { silent: true, quiet: !!quiet };
    // A repair that started before this write finished read the cart too early; let it finish,
    // then check again, so the cart handed back always includes this write.
    var pending = cartRepairPromise;
    var check = pending
      ? pending.catch(function () {}).then(function () { return repairCartLimits(options); })
      : repairCartLimits(options);
    return check.catch(function () {
      return null;
    }).then(function (result) {
      // 2026-10-25 speed: hand the checked cart to the caller so it doesn't read /cart.js again.
      if (result && result.cart) { try { response.bzCart = result.cart; } catch (_e) {} }
      return response;
    });
  }

  function registerVariant(variantId, handle, limit) {
    variantId = idString(variantId);
    handle = (handle || '').trim();
    if (variantId && handle) variantToHandle[variantId] = handle;
    if (variantId && limit != null && limit !== '') {
      var n = toInt(limit, null);
      if (n != null && n >= 0) variantFallbackLimits[variantId] = n;
    }
  }

  function closestHandle(el) {
    while (el && el !== document) {
      if (el.getAttribute) {
        var handle = el.getAttribute('data-bz-product-handle') ||
          el.getAttribute('data-product-handle') ||
          el.getAttribute('data-handle');
        if (handle) return handle;
      }
      el = el.parentNode;
    }
    return '';
  }

  function resolveHandle(variantId, sourceElement, item) {
    return (item && (item.handle || item.productHandle || item.product_handle)) ||
      (sourceElement && closestHandle(sourceElement)) ||
      variantToHandle[idString(variantId)] ||
      '';
  }

  function scan(root) {
    root = root || document;
    var nodes = root.querySelectorAll(
      '[data-bz-variant-id], [data-variant-id], input[name="id"], option[value]'
    );
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      var variantId = node.getAttribute('data-bz-variant-id') ||
        node.getAttribute('data-variant-id') ||
        (node.name === 'id' ? node.value : '') ||
        (node.tagName === 'OPTION' ? node.value : '');
      var handle = node.getAttribute('data-bz-product-handle') ||
        node.getAttribute('data-product-handle') ||
        node.getAttribute('data-handle') ||
        closestHandle(node);
      var limit = node.getAttribute('data-bz-inventory-quantity') ||
        node.getAttribute('data-inventory-quantity') ||
        (node.getAttribute('data-bz-variant-id') || node.getAttribute('data-variant-id') ? node.getAttribute('max') : '');
      registerVariant(variantId, handle, limit);
    }
  }

  function fetchProduct(handle) {
    if (!handle) return Promise.resolve(null);
    var cached = productCache[handle];
    if (cached && Date.now() - cached.at < PRODUCT_CACHE_MS) return cached.promise;
    var promise = nativeFetch(productUrl(handle), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    }).then(function (response) {
      if (!response.ok) throw new Error('Could not read live inventory.');
      return response.json();
    }).then(function (product) {
      var variants = product && product.variants ? product.variants : [];
      for (var i = 0; i < variants.length; i++) registerVariant(variants[i].id, handle);
      return product;
    });
    productCache[handle] = { at: Date.now(), promise: promise };
    return promise;
  }

  function getVariant(handle, variantId) {
    variantId = idString(variantId);
    if (!handle || !variantId) return Promise.resolve(null);
    return fetchProduct(handle).then(function (product) {
      var variants = product && product.variants ? product.variants : [];
      for (var i = 0; i < variants.length; i++) {
        if (idString(variants[i].id) === variantId) {
          variants[i]._bzFallbackLimit = variantFallbackLimits[variantId];
          return variants[i];
        }
      }
      return null;
    }).catch(function () {
      return null;
    });
  }

  function stockLimit(variant) {
    if (!variant) return null;
    if (variant.inventory_management === 'shopify' && variant.inventory_policy !== 'continue') {
      var fallback = variant._bzFallbackLimit;
      var live = toInt(variant.inventory_quantity, null);
      if (live == null) return fallback != null ? Math.max(0, fallback) : null;
      if (live <= 0 && variant.available && fallback > 0) return fallback;
      return Math.max(0, live);
    }
    return null;
  }

  function getCart() {
    if (!cartPromise) {
      cartPromise = nativeFetch('/cart.js', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      }).then(function (response) {
        if (!response.ok) throw new Error('Could not read cart.');
        return response.json();
      }).catch(function () {
        return { items: [] };
      }).then(function (cart) {
        setTimeout(function () { cartPromise = null; }, 0);
        return cart;
      });
    }
    return cartPromise;
  }

  function cartQuantities() {
    return getCart().then(function (cart) {
      var quantities = Object.create(null);
      var items = cart && cart.items ? cart.items : [];
      for (var i = 0; i < items.length; i++) {
        var id = idString(items[i].variant_id || items[i].id);
        if (!id) continue;
        quantities[id] = (quantities[id] || 0) + toInt(items[i].quantity, 0);
        if (items[i].handle) registerVariant(id, items[i].handle);
      }
      return quantities;
    });
  }

  function lineItem(line) {
    return getCart().then(function (cart) {
      var items = cart && cart.items ? cart.items : [];
      return items[Math.max(0, toInt(line, 1) - 1)] || null;
    });
  }

  function shopifyItem(item, preserveHandle) {
    var clean = {
      id: toInt(item.id || item.variant_id, 0),
      quantity: toInt(item.quantity, 1)
    };
    if (item.properties && typeof item.properties === 'object') clean.properties = item.properties;
    if (item.selling_plan) clean.selling_plan = item.selling_plan;
    if (preserveHandle && (item.handle || item.productHandle || item.product_handle)) {
      clean.handle = item.handle || item.productHandle || item.product_handle;
    }
    return clean;
  }

  function inventoryMessage(limit) {
    if (limit <= 0) return 'Sold out - quantity updated.';
    return 'Only ' + limit + ' available - quantity updated.';
  }

  function unknownQuantityMessage(limit) {
    return 'Quantity capped at ' + limit + ' until stock is verified.';
  }

  function showToast(message) {
    if (!message || !document.body) return;
    var toast = document.createElement('div');
    toast.className = 'bz-inventory-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.textContent = message;
    document.body.appendChild(toast);
    requestAnimationFrame(function () { toast.classList.add('is-visible'); });
    setTimeout(function () {
      toast.classList.remove('is-visible');
      setTimeout(function () {
        if (toast.parentNode) toast.parentNode.removeChild(toast);
      }, 220);
    }, 3600);
  }

  function showInlineError(sourceElement, message) {
    if (!sourceElement || !message) {
      return false;
    }
    var host = sourceElement.closest &&
      sourceElement.closest('[data-bz-product], .bz-cart-row, [data-bz-cart], .bz-tile, [data-bz-hz], [data-qhz], form');
    var errorEl = host && host.querySelector &&
      host.querySelector('[data-bz-product-error], [data-bz-cart-error], [data-bz-inventory-error]');
    if (!errorEl && sourceElement.parentNode) {
      errorEl = document.createElement('div');
      errorEl.className = 'bz-inventory-error';
      errorEl.setAttribute('data-bz-inventory-error', '');
      errorEl.setAttribute('role', 'alert');
      sourceElement.parentNode.insertBefore(errorEl, sourceElement.nextSibling);
    }
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.hidden = false;
      clearTimeout(errorEl._bzInventoryTimer);
      errorEl._bzInventoryTimer = setTimeout(function () {
        errorEl.hidden = true;
      }, 7000);
      return true;
    } else {
      return false;
    }
  }

  function showFeedback(sourceElement, message) {
    showInlineError(sourceElement, message);
    showToast(message);
  }

  function clampAddItems(items, options) {
    options = options || {};
    var sourceElement = options.sourceElement || null;
    var deferFeedback = options.deferFeedback === true;
    var preserveHandles = options.preserveHandles === true;
    var normalized = [];
    for (var i = 0; i < (items || []).length; i++) {
      var raw = items[i] || {};
      var id = toInt(raw.id || raw.variant_id, 0);
      if (!id) continue;
      var hasQuantity = raw.quantity != null && raw.quantity !== '';
      var qty = toInt(raw.quantity, 1);
      if (hasQuantity && qty < 1) {
        var badQty = new GuardError('Quantity must be at least 1.');
        showFeedback(sourceElement, badQty.message);
        return Promise.reject(badQty);
      }
      if (qty < 1) qty = 1;
      normalized.push({
        raw: raw,
        id: id,
        quantity: qty,
        handle: resolveHandle(id, sourceElement, raw)
      });
    }
    if (!normalized.length) return Promise.reject(new GuardError('Nothing to add.'));

    // 2026-10-25 speed: start each product's live-stock read now, alongside the cart read, instead
    // of one after the other. They are cached for PRODUCT_CACHE_MS, so the loop below picks them up.
    // Same checks, same decisions, no waiting in line.
    normalized.forEach(function (item) { if (item.handle) getVariant(item.handle, item.id); });
    return cartQuantities().then(function (inCart) {
      var planned = Object.create(null);
      var warnings = [];
      var chain = Promise.resolve();

      normalized.forEach(function (item) {
        chain = chain.then(function () {
          if (!item.handle) {
            if (item.quantity > UNKNOWN_VARIANT_MAX_QTY) {
              item.quantity = UNKNOWN_VARIANT_MAX_QTY;
              warnings.push(unknownQuantityMessage(UNKNOWN_VARIANT_MAX_QTY));
            }
            var unknownKey = idString(item.id);
            planned[unknownKey] = (planned[unknownKey] || 0) + item.quantity;
            return;
          }
          return getVariant(item.handle, item.id).then(function (variant) {
            var limit = stockLimit(variant);
            if (limit == null) return;
            var key = idString(item.id);
            var already = (inCart[key] || 0) + (planned[key] || 0);
            var allowed = limit - already;
            if (allowed <= 0) {
              var stopMessage = limit > 0
                ? 'Only ' + limit + ' available - your cart already has the limit.'
                : 'This item is sold out.';
              warnings.push(stopMessage);
              showFeedback(sourceElement, stopMessage);
              throw new GuardError(stopMessage, warnings);
            }
            if (item.quantity > allowed) {
              item.quantity = allowed;
              warnings.push(inventoryMessage(limit));
            }
            planned[key] = (planned[key] || 0) + item.quantity;
          });
        });
      });

      return chain.then(function () {
        var clamped = normalized.map(function (item) {
          var merged = {};
          for (var key in item.raw) {
            if (Object.prototype.hasOwnProperty.call(item.raw, key) &&
                key !== 'handle' && key !== 'productHandle' && key !== 'product_handle') {
              merged[key] = item.raw[key];
            }
          }
          merged.id = item.id;
          merged.quantity = item.quantity;
          if (preserveHandles && item.handle) merged.handle = item.handle;
          return shopifyItem(merged, preserveHandles);
        });
        if (warnings.length && !deferFeedback) {
          var message = warnings[0];
          showFeedback(sourceElement, message);
        }
        return { items: clamped, warnings: warnings };
      });
    });
  }

  function clampCartQuantity(config) {
    config = config || {};
    var requested = toInt(config.quantity, 0);
    if (requested < 0) {
      var neg = new GuardError('Quantity cannot be negative.');
      showFeedback(config.sourceElement, neg.message);
      return Promise.reject(neg);
    }
    if (requested === 0) return Promise.resolve({ quantity: 0, warnings: [] });

    var variantId = idString(config.variantId);
    var handle = config.handle || '';
    var linePromise = Promise.resolve(null);
    if ((!variantId || !handle) && config.line) linePromise = lineItem(config.line);

    return linePromise.then(function (item) {
      if (item) {
        variantId = variantId || idString(item.variant_id || item.id);
        handle = handle || item.handle || resolveHandle(variantId, config.sourceElement, item);
      }
      handle = handle || resolveHandle(variantId, config.sourceElement, config);
      if (!variantId || !handle) return { quantity: requested, warnings: [] };
      return getVariant(handle, variantId).then(function (variant) {
        var limit = stockLimit(variant);
        if (limit == null || requested <= limit) return { quantity: requested, warnings: [] };
        var message = inventoryMessage(limit);
        showFeedback(config.sourceElement, message);
        return { quantity: limit, warnings: [message] };
      });
    });
  }

  function parseJsonBody(body) {
    if (typeof body !== 'string') return null;
    try { return JSON.parse(body); } catch (_e) { return null; }
  }

  function isUrlParams(body) {
    return window.URLSearchParams && body instanceof URLSearchParams;
  }

  function isFormBody(body) {
    return (window.FormData && body instanceof FormData) || isUrlParams(body);
  }

  // 2026-10-25 speed: a /cart/update.js that only writes attributes, the note or a discount code
  // (ride-along codes, Size aim, the ship date) can't change a quantity, so there is nothing to
  // clamp before it or repair after it.
  function isAttributeOnlyUpdate(init) {
    var body = init && init.body;
    if (!body) return false;
    if (isFormBody(body)) {
      var only = true;
      body.forEach(function (_value, key) {
        if (!/^(attributes\[.*\]|note|discount)$/.test(key)) only = false;
      });
      return only;
    }
    var payload = parseJsonBody(body);
    if (!payload || typeof payload !== 'object') return false;
    for (var key in payload) {
      if (Object.prototype.hasOwnProperty.call(payload, key) &&
          key !== 'attributes' && key !== 'note' && key !== 'discount') return false;
    }
    return true;
  }

  // 2026-10-25 speed: for a caller that already guarded its items and broadcasts on its own
  // (bz-cart-add), hand the response back the moment Shopify answers. The repair still runs before
  // the next cart write leaves the queue; its cart arrives on response.bzChecked (a promise that
  // resolves to the checked cart, or null) and on response.bzCart.
  function queueEarly(input, init) {
    return new Promise(function (resolve, reject) {
      queueCartMutation(function () {
        return nativeFetch(input, init).then(function (response) {
          if (!response || !response.ok) { resolve(response); return response; }
          var check = verifyCartMutation(response, true);
          try { response.bzChecked = check.then(function () { return response.bzCart || null; }); } catch (_e) {}
          resolve(response);
          return check;
        }, function (error) {
          reject(error);
          throw error;
        });
      }).catch(function () {});
    });
  }

  function cloneInit(init) {
    var next = {};
    init = init || {};
    for (var key in init) {
      if (Object.prototype.hasOwnProperty.call(init, key)) next[key] = init[key];
    }
    return next;
  }

  function guardAddRequest(input, init) {
    var next = cloneInit(init);
    var body = next.body;
    if (!body) return Promise.resolve({ input: input, init: next });

    if (isFormBody(body)) {
      var formItem = {
        id: body.get('id'),
        quantity: body.get('quantity') || 1,
        handle: body.get('product_handle') || ''
      };
      return clampAddItems([formItem]).then(function (result) {
        body.set('quantity', String(result.items[0].quantity));
        if (typeof body.delete === 'function') body.delete('product_handle');
        return { input: input, init: next };
      });
    }

    var payload = parseJsonBody(body);
    if (!payload) return Promise.resolve({ input: input, init: next });

    if (payload.items) {
      return clampAddItems(payload.items).then(function (result) {
        payload.items = result.items;
        next.body = JSON.stringify(payload);
        return { input: input, init: next };
      });
    }

    if (payload.id || payload.variant_id) {
      return clampAddItems([payload]).then(function (result) {
        payload.quantity = result.items[0].quantity;
        next.body = JSON.stringify(payload);
        return { input: input, init: next };
      });
    }

    return Promise.resolve({ input: input, init: next });
  }

  function guardUpdateRequest(input, init) {
    var next = cloneInit(init);
    var body = next.body;
    var payload = parseJsonBody(body);
    if (!payload || !payload.updates || typeof payload.updates !== 'object') {
      return Promise.resolve({ input: input, init: next });
    }

    var keys = Object.keys(payload.updates);
    var arrayUpdates = Array.isArray(payload.updates);
    var chain = Promise.resolve();
    keys.forEach(function (key) {
      chain = chain.then(function () {
        return clampCartQuantity({
          quantity: payload.updates[key],
          line: arrayUpdates ? toInt(key, 0) + 1 : null,
          variantId: arrayUpdates ? '' : key
        }).then(function (result) {
          payload.updates[key] = result.quantity;
        });
      });
    });

    return chain.then(function () {
      next.body = JSON.stringify(payload);
      return { input: input, init: next };
    });
  }

  function guardChangeRequest(input, init) {
    var next = cloneInit(init);
    var body = next.body;
    if (isFormBody(body)) {
      if (body.get('quantity') == null) return Promise.resolve({ input: input, init: next });
      return clampCartQuantity({
        quantity: body.get('quantity'),
        line: body.get('line'),
        variantId: body.get('id') || body.get('variant_id')
      }).then(function (result) {
        body.set('quantity', String(result.quantity));
        return { input: input, init: next };
      });
    }

    var payload = parseJsonBody(body);
    if (!payload || payload.quantity == null) return Promise.resolve({ input: input, init: next });
    return clampCartQuantity({
      quantity: payload.quantity,
      line: payload.line,
      variantId: payload.id || payload.variant_id
    }).then(function (result) {
      payload.quantity = result.quantity;
      next.body = JSON.stringify(payload);
      return { input: input, init: next };
    });
  }

  function fetchUpdatedCart() {
    cartPromise = null;
    return getCart();
  }

  function dispatchCartUpdate(cart, source) {
    if (!document || !document.dispatchEvent) return;
    var itemCount = cart && typeof cart.item_count === 'number' ? cart.item_count : 0;
    try {
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart || { item_count: itemCount, items: [] },
          sourceId: '',
          data: {
            source: source || 'bz-inventory-repair',
            itemCount: itemCount
          }
        }
      }));
    } catch (_e) {}
  }

  function repairCartLimits(options) {
    options = options || {};
    if (cartRepairPromise) return cartRepairPromise;

    cartRepairPromise = fetchUpdatedCart().then(function (cart) {
      var items = cart && cart.items ? cart.items : [];
      var checks = Promise.resolve();
      var corrections = [];

      // 2026-10-25 speed: check every line at once instead of one after another (each live-stock read
      // is cached for PRODUCT_CACHE_MS). Corrections are sorted by line below, so the order they come
      // back in doesn't matter.
      checks = Promise.all(items.map(function (item, index) {
        var variantId = idString(item.variant_id || item.id);
        var handle = item.handle || resolveHandle(variantId, null, item);
        if (!variantId || !handle) return null;
        registerVariant(variantId, handle);
        return getVariant(handle, variantId).then(function (variant) {
          var limit = stockLimit(variant);
          var quantity = toInt(item.quantity, 0);
          if (limit == null || quantity <= limit) return;
          corrections.push({
            line: index + 1,
            quantity: Math.max(0, limit),
            limit: Math.max(0, limit)
          });
        });
      }));

      return checks.then(function () {
        if (!corrections.length) {
          var sig = cartSig(cart);
          if (!options.quiet && cart && typeof cart.item_count === 'number' && sig !== lastHeardSig) {
            dispatchCartUpdate(cart, 'bz-inventory-sync');
          }
          return { cart: cart, corrections: [] };
        }
        corrections.sort(function (a, b) { return b.line - a.line; });
        var apply = Promise.resolve();
        corrections.forEach(function (correction) {
          apply = apply.then(function () {
            return nativeFetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              credentials: 'same-origin',
              body: JSON.stringify({ line: correction.line, quantity: correction.quantity })
            });
          });
        });
        return apply.then(fetchUpdatedCart).then(function (updatedCart) {
          if (!options.silent) showToast(inventoryMessage(corrections[0].limit));
          dispatchCartUpdate(updatedCart, 'bz-inventory-repair');
          if (options.reloadCartPage && window.location && /\/cart\/?$/.test(window.location.pathname)) {
            setTimeout(function () { window.location.reload(); }, 350);
          }
          return { cart: updatedCart, corrections: corrections };
        });
      });
    }).then(function (result) {
      cartRepairPromise = null;
      return result;
    }, function (error) {
      cartRepairPromise = null;
      throw error;
    });

    return cartRepairPromise;
  }

  function maybeRepairCartOnLoad() {
    var hasVisibleCartCount = false;
    try {
      hasVisibleCartCount = !!document.querySelector('.cart-bubble:not(.visually-hidden), [data-cart-count]:not([hidden])');
    } catch (_e) {}
    if ((window.location && /\/cart\/?$/.test(window.location.pathname)) || hasVisibleCartCount) {
      repairCartLimits({ silent: true, reloadCartPage: true }).catch(function () {});
    }
  }

  window.bzInventoryGuard = {
    registerVariant: registerVariant,
    scan: scan,
    resolveHandle: resolveHandle,
    fetchProduct: fetchProduct,
    getVariant: getVariant,
    stockLimit: stockLimit,
    clampAddItems: clampAddItems,
    clampCartQuantity: clampCartQuantity,
    repairCartLimits: repairCartLimits,
    // 2026-10-25 speed: warm a product's live-stock read (kept PRODUCT_CACHE_MS) before the Add click.
    prefetch: function (handle) { if (handle) fetchProduct(handle).catch(function () {}); },
    showToast: showToast,
    showInlineError: showInlineError,
    GuardError: GuardError
  };

  window.fetch = function (input, init) {
    var url = requestUrl(input);
    if (init && init.bzInventoryGuarded) {
      if (isCartAdd(url) || isCartChange(url) || isCartUpdate(url)) {
        if (init.bzQuiet) return queueEarly(input, init);
        return queueCartMutation(function () {
          return nativeFetch(input, init).then(function (response) { return verifyCartMutation(response, false); });
        });
      }
      return nativeFetch(input, init);
    }
    // Attribute-only writes still wait their turn in the queue, so they never race a quantity change.
    if (isCartUpdate(url) && isAttributeOnlyUpdate(init)) {
      return queueCartMutation(function () { return nativeFetch(input, init); });
    }
    if (isCartAdd(url)) {
      // CRITICAL live-animal guard: every storefront add path must pass through
      // the same inventory clamp, not just Big Z's helper. Native Shopify forms,
      // quick-add modals, and direct scripts all use /cart/add.js.
      return queueCartMutation(function () {
        return guardAddRequest(input, init).then(function (guarded) {
          guarded.init.bzInventoryGuarded = true;
          return nativeFetch(guarded.input, guarded.init).then(verifyCartMutation);
        });
      });
    }
    if (isCartChange(url)) {
      return queueCartMutation(function () {
        return guardChangeRequest(input, init).then(function (guarded) {
          guarded.init.bzInventoryGuarded = true;
          return nativeFetch(guarded.input, guarded.init).then(verifyCartMutation);
        });
      });
    }
    if (isCartUpdate(url)) {
      return queueCartMutation(function () {
        return guardUpdateRequest(input, init).then(function (guarded) {
          guarded.init.bzInventoryGuarded = true;
          return nativeFetch(guarded.input, guarded.init).then(verifyCartMutation);
        });
      });
    }
    return nativeFetch(input, init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scan(document);
      maybeRepairCartOnLoad();
    });
  } else {
    scan(document);
    maybeRepairCartOnLoad();
  }
  document.addEventListener('shopify:section:load', function (event) { scan(event.target || document); });
})();
